import { Avatar, Button, Divider, Stack, Typography, useMediaQuery } from "@mui/material"
import { IconLike } from "../../../../icons/IconLike"
import { COLORS } from "../../../../styles/variables"
import { AvatarStyle, ButtonStyle, StackWithComment, TypAnswer, TypDate, TypNameLastName } from "./style"
import { ReplyItem, type OpenL2ReplyThreadPayload } from "../ReplyItem"
import { RepliesSummary } from "../RepliesSummary"
import { formatNumber } from "../../../../common/FormatNumber"
import { PureLightTheme } from "../../../../styles/theme"
import { CommentData } from "../../../../types"
import { ReplyForm } from "../ReplyForm"
import { useFormContext } from "react-hook-form"
import { formatApiDateTimeComment } from "../../../../utils/common"
import { MainStack as ReplyBranchIndent } from "../ReplyItem/style"
import { CommentEditableText, type CommentEditState } from "../CommentEditableText"
import type { CommentsSortUi } from "../../../../api/types/projects"
import { useSetCommentReactionMutation } from "../../../../api/projects"
import { useSetGrowthToolCommentReactionMutation } from "../../../../api/growthTools"
import { useSetSocialOrderCommentReactionMutation } from "../../../../api/socialOrders"
import { IconLikeFilled } from "../../../../icons/IconLikeFilled"
import { getCommentHighlightRowSx } from "../../helpers/commentHighlightSx"
import { openPlatformReportModal } from "../../../Modals"

type CommentItemProps = {
    comment: CommentData
    activeCommentId: number | null
    setActiveCommentId: (id: number | null) => void
    activeReplyTarget: { firstName: string; lastName: string; parentId: number } | null
    setActiveReplyTarget: (target: { firstName: string; lastName: string; parentId: number } | null) => void
    onSubmitReply?: (payload: { parentId: number; text: string }) => Promise<boolean>
    isSubmittingComment?: boolean
    loadMoreReplies?: (parentId: number) => void | Promise<void>
    loadingParentId?: number | null
    currentUserId?: number | null
    editState: CommentEditState
    onBeginEdit: (commentId: number, originalText: string) => void
    cancelEdit: () => void
    setDraft: (value: string) => void
    confirmSave: () => void | Promise<void>
    isUpdatingComment: boolean
    onDeleteComment?: (commentId: number) => Promise<boolean>
    onOpenReplyThread?: (p: OpenL2ReplyThreadPayload) => void
    commentReactionCache?:
        | { kind?: 'project'; projectId: number; type: string; sort?: CommentsSortUi; threadParentId?: number }
        | { kind: 'growthTool'; growthToolId: number; type: string; sort?: CommentsSortUi; threadParentId?: number }
        | { kind: 'socialOrder'; socialOrderId: number; type: string; sort?: CommentsSortUi; threadParentId?: number }
    highlightCommentId?: number | null
}

export const CommentItem = ({
    comment,
    activeCommentId,
    setActiveCommentId,
    activeReplyTarget,
    setActiveReplyTarget,
    onSubmitReply,
    isSubmittingComment,
    loadMoreReplies,
    loadingParentId,
    currentUserId,
    editState,
    onBeginEdit,
    cancelEdit,
    setDraft,
    confirmSave,
    isUpdatingComment,
    onDeleteComment,
    onOpenReplyThread,
    commentReactionCache,
    highlightCommentId,
}: CommentItemProps) => {
    const highlightSx = getCommentHighlightRowSx(highlightCommentId === comment.id)
    const isTablet = useMediaQuery(PureLightTheme.breakpoints.down("md"))
    const { setValue, setFocus, getValues } = useFormContext()
    const [setProjectReaction, { isLoading: isProjectReacting }] = useSetCommentReactionMutation()
    const [setGrowthToolReaction, { isLoading: isGrowthToolReacting }] = useSetGrowthToolCommentReactionMutation()
    const [setSocialOrderReaction, { isLoading: isSocialOrderReacting }] = useSetSocialOrderCommentReactionMutation()
    const isReacting = isProjectReacting || isGrowthToolReacting || isSocialOrderReacting

    const isEditing = editState?.commentId === comment.id
    const canEdit = currentUserId != null && comment.authorId === currentUserId
    const canDelete = comment.canDelete === true

    const handleDelete = async () => {
        if (!onDeleteComment) return
        await onDeleteComment(comment.id)
    }

    const handleReplyClick = (parentId: number, firstName: string, lastName: string) => {
        if (isTablet) {
            setValue("commentText", `${firstName}, `)
            setValue("mobileReplyTarget", {
                id: parentId,
                name: `${firstName} ${lastName}`,
            })
            setFocus("commentText")
        } else {
            setActiveCommentId(comment.id)
            setActiveReplyTarget({ firstName, lastName, parentId })
        }
    }

    const handleDesktopReplySubmit = async () => {
        if (!onSubmitReply || !activeReplyTarget) return
        const text = getValues("replyText")
        const success = await onSubmitReply({ parentId: activeReplyTarget.parentId, text })
        if (success) {
            setActiveCommentId(null)
            setActiveReplyTarget(null)
            setValue("replyText", "")
        }
    }

    const handleHeartToggle = async () => {
        const value = comment.likedByMe ? 0 : 1
        try {
            await (commentReactionCache?.kind === 'growthTool'
                ? setGrowthToolReaction({
                    commentId: comment.id,
                    body: { value },
                    cache: commentReactionCache,
                }).unwrap()
                : commentReactionCache?.kind === 'socialOrder'
                  ? setSocialOrderReaction({
                      commentId: comment.id,
                      body: { value },
                      cache: commentReactionCache,
                  }).unwrap()
                  : setProjectReaction({
                      commentId: comment.id,
                      body: { value },
                      ...(commentReactionCache ? { cache: commentReactionCache } : {}),
                  }).unwrap())
        } catch {
            // no-op
        }
    }

    return (
        <Stack id={`comment-${comment.id}`} sx={highlightSx} gap={0}>
            <Stack direction="row">
                <Avatar src={comment.avatarUrl} sx={AvatarStyle} />
                <Stack sx={StackWithComment}>
                    <Typography variant="cerulean-14-semiBold" sx={TypNameLastName}>
                        {comment.firstName} {comment.lastName}
                    </Typography>
                    <CommentEditableText
                        nodeId={comment.id}
                        text={comment.text}
                        editState={editState}
                        setDraft={setDraft}
                        onConfirmSave={confirmSave}
                        onCancelEdit={cancelEdit}
                        isUpdatingComment={isUpdatingComment}
                    />
                    {isTablet && (
                        <Typography variant="pictonBlue-12-normal" sx={TypDate}>
                            {formatApiDateTimeComment(comment.date)}
                        </Typography>
                    )}
                    <Stack direction="row" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
                            {!isTablet && (
                                <Typography variant="pictonBlue-12-normal" sx={TypDate}>
                                    {formatApiDateTimeComment(comment.date)}
                                </Typography>
                            )}
                            {!isEditing && (
                                <>
                                    <Typography
                                        variant="pictonBlue-12-normal"
                                        sx={TypAnswer}
                                        onClick={() =>
                                            handleReplyClick(comment.id, comment.firstName, comment.lastName)
                                        }
                                    >
                                        Ответить
                                    </Typography>
                                    {canEdit ? (
                                        <Typography
                                            variant="pictonBlue-12-normal"
                                            sx={TypAnswer}
                                            onClick={() => onBeginEdit(comment.id, comment.text)}
                                        >
                                            Изменить
                                        </Typography>
                                    ) : null}
                                    {canDelete ? (
                                        <Typography
                                            variant="pictonBlue-12-normal"
                                            sx={TypAnswer}
                                            onClick={() => void handleDelete()}
                                        >
                                            Удалить
                                        </Typography>
                                    ) : null}
                                    {currentUserId != null && comment.authorId !== currentUserId ? (
                                        <Typography
                                            variant="pictonBlue-12-normal"
                                            sx={TypAnswer}
                                            onClick={() =>
                                                openPlatformReportModal({
                                                    targetType: 'comment',
                                                    targetId: comment.id,
                                                    dialogTitle: 'Пожаловаться на комментарий',
                                                })
                                            }
                                        >
                                            Пожаловаться
                                        </Typography>
                                    ) : null}
                                </>
                            )}
                        </Stack>
                        <Stack direction="row" gap={2}>
                            <Button
                                variant="text"
                                disableRipple
                                sx={ButtonStyle}
                                disabled={isReacting}
                                onClick={() => void handleHeartToggle()}
                                aria-label={comment.likedByMe ? "Снять лайк" : "Поставить лайк"}
                            >
                                <Stack direction="row" gap={0.5} alignItems="center">
                                    {comment.likedByMe ? 
                                        <IconLikeFilled sx={{ width: 16, height: 16, color: COLORS.CERULEAN }} /> : 
                                        <IconLike sx={{ width: 16, height: 16, color: COLORS.CERULEAN }} />
                                    }
                                    {comment.likes > 0 ? (
                                        <Typography 
                                            variant="pictonBlue-12-medium"
                                            sx={{ lineHeight: 0 }}
                                        >{formatNumber(comment.likes)}</Typography>
                                    ) : null}
                                </Stack>
                            </Button>
                        </Stack>
                    </Stack>
                    {!comment.repliesSummary && <Divider sx={{ mt: "3px" }} />}
                </Stack>
            </Stack>
            {comment.replies ? (
                comment.replies.map((reply) => (
                    <ReplyItem
                        key={reply.id}
                        reply={reply}
                        replyDepth={1}
                        onReplyTo={handleReplyClick}
                        loadMoreReplies={loadMoreReplies}
                        loadingParentId={loadingParentId}
                        currentUserId={currentUserId}
                        editState={editState}
                        onBeginEdit={onBeginEdit}
                        cancelEdit={cancelEdit}
                        setDraft={setDraft}
                        confirmSave={confirmSave}
                        isUpdatingComment={isUpdatingComment}
                        onDeleteComment={onDeleteComment}
                        onOpenReplyThread={onOpenReplyThread}
                        commentReactionCache={commentReactionCache}
                        highlightCommentId={highlightCommentId}
                    />
                ))
            ) : comment.repliesSummary ? (
                <RepliesSummary repliesSummary={comment.repliesSummary} />
            ) : null}
            {comment.hasMoreChildren && loadMoreReplies ? (
                <Stack sx={{ ...ReplyBranchIndent, mt: 0.5 }}>
                    <Button
                        variant="text"
                        disableRipple
                        sx={{ ...ButtonStyle, textTransform: "none", justifyContent: "flex-start" }}
                        disabled={loadingParentId === comment.id}
                        onClick={() => void loadMoreReplies(comment.id)}
                    >
                        <Typography variant="pictonBlue-12-normal" sx={TypAnswer}>
                            Показать ещё
                        </Typography>
                    </Button>
                </Stack>
            ) : null}

            {!isTablet && activeCommentId === comment.id && activeReplyTarget && !isEditing && (
                <ReplyForm
                    activeReplyTarget={activeReplyTarget}
                    onSend={handleDesktopReplySubmit}
                    isSubmitting={isSubmittingComment}
                    onClose={() => {
                        setActiveCommentId(null)
                        setActiveReplyTarget(null)
                        setValue("replyText", "")
                    }}
                />
            )}
        </Stack>
    )
}
